// Taste feedback taps (R-14 app half): more like this / less like this / not for me (+ an optional reason), all through the
// `signal` edge function (supabase/functions/signal/index.ts). Not for me also writes the swipes -2 every ranker already
// excludes, so the server stops showing it at once; this store hides it on this device the same moment and drops the
// session's cached Browse answers, so neither the list on screen nor the next Browse load (which reads that cache first)
// shows it again (Alvin 10/08: "Not interested" must count IMMEDIATELY).
import { useSyncExternalStore } from 'react'
import * as Sentry from '@sentry/react'
import { callFn } from './api'
import { markBrowseStale } from './browseCache'
import { onOwnerChange } from './owner'
import type { BrowseRes, Card } from './browse'
import type { NfmReason } from './checkinCore'

export type Mark = 'more' | 'less'
export type Ctx = { row?: string; position?: number; explore?: boolean; source: 'card' | 'detail' }
// status: 'undoing' while an Undo is on its way, 'back' once it worked (the panel says so, then closes), 'undo_error' if it failed
type Last = { id: string; title: string; status: 'sent' | 'error' | 'undoing' | 'back' | 'undo_error'; reason: NfmReason | null; reasonSent: boolean; at: number }
type State = { marks: Record<string, Mark>; hidden: Set<string>; last: Last | null }

const HIDDEN_KEY = 'mg_hidden'
const MARKS_KEY = 'mg_taste_marks'
const MAX_KEPT = 500
const readJson = <T,>(k: string, d: T): T => {
  try { const v = localStorage.getItem(k); return v ? (JSON.parse(v) as T) : d } catch { return d }
}
const writeJson = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)) } catch { /* convenience only: the server is the record */ } }

let state: State = { marks: readJson<Record<string, Mark>>(MARKS_KEY, {}), hidden: new Set(readJson<string[]>(HIDDEN_KEY, [])), last: null }
const subs = new Set<() => void>()
const set = (p: Partial<State>) => { state = { ...state, ...p }; subs.forEach((f) => f()) }
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }
export const useTaste = () => useSyncExternalStore(subscribe, () => state)
export const isHidden = (id: string) => state.hidden.has(id)
// a hidden card leaves the list at once and the next one slides up under the finger: a second tap of a quick double tap
// must not land on it (blind test 10/13, Kim). Card buttons ignore taps this soon after a hide.
const SETTLE_MS = 600
let hidAt = 0
export const justHid = () => Date.now() - hidAt < SETTLE_MS

const context = (c: Ctx) => ({
  ...(c.row && /^[a-z_]{1,32}$/.test(c.row) ? { row_key: c.row } : {}),
  ...(Number.isInteger(c.position) ? { position: c.position } : {}),
  ...(c.explore ? { explore: true } : {}),
})
export const signal = (body: Record<string, unknown>) => callFn<{ ok?: boolean; id?: number; y_axis?: string }>('signal', body)

/** Drop every Browse answer this session cached (lib/browse.ts `mg_browse:`), so the next load asks the server. */
export function dropBrowseCache() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i)
      if (k?.startsWith('mg_browse:')) sessionStorage.removeItem(k)
    }
  } catch { /* nothing cached */ }
}

/** More / less like this. A second tap on the same one does nothing (it was heard); the other one replaces it. */
export async function mark(itemId: string, m: Mark, ctx: Ctx): Promise<void> {
  if (state.marks[itemId] === m) return
  const prev = state.marks[itemId]
  const marks = { ...state.marks, [itemId]: m }
  set({ marks })
  try {
    await signal({ kind: m === 'more' ? 'more_like' : 'less_like', item: itemId, source: ctx.source, context: context(ctx) })
    markBrowseStale()
    const keep = Object.entries(state.marks).slice(-MAX_KEPT)
    writeJson(MARKS_KEY, Object.fromEntries(keep))
  } catch (e) {
    Sentry.captureException(e)
    const back = { ...state.marks }
    if (prev) back[itemId] = prev
    else delete back[itemId]
    set({ marks: back })
    throw e
  }
}

/**
 * B-155: tapping a selected More / Less like this again takes it back here (the button goes back to unselected, kept on
 * reload), and the server takes the earlier signal back (signal `unmark`).
 */
export function unmark(itemId: string): void {
  if (!state.marks[itemId]) return
  const marks = { ...state.marks }
  delete marks[itemId]
  set({ marks })
  writeJson(MARKS_KEY, Object.fromEntries(Object.entries(marks).slice(-MAX_KEPT)))
  // the server takes the earlier more_like / less_like back (signal `unmark`, lane E)
  void signal({ kind: 'unmark', item: itemId }).then(() => markBrowseStale(), (e) => Sentry.captureException(e))
}

/** Not for me: gone from every list at once, then the server hears it. A failure keeps it hidden here and offers a retry. */
export function notForMe(item: { id: string; title: string }, ctx: Ctx): void {
  hidAt = Date.now()
  const hidden = new Set(state.hidden)
  hidden.add(item.id)
  writeJson(HIDDEN_KEY, [...hidden].slice(-MAX_KEPT))
  dropBrowseCache()
  set({ hidden, last: { id: item.id, title: item.title, status: 'sent', reason: null, reasonSent: false, at: Date.now() } })
  lastCtx = ctx
  void send(item.id, null)
}
let lastCtx: Ctx = { source: 'card' }
// the Not for me signals still on their way, per item: an Undo waits for them so it never lands before the thing it undoes
const pending = new Map<string, Promise<unknown>>()

async function send(id: string, reason: NfmReason | null) {
  const p = signal({ kind: 'not_for_me', item: id, ...(reason ? { reason } : {}), source: lastCtx.source, context: context(lastCtx) })
  const all = Promise.allSettled([pending.get(id), p])
  pending.set(id, all)
  void all.then(() => { if (pending.get(id) === all) pending.delete(id) })
  try {
    await p
    if (state.last?.id === id && (state.last.status === 'sent' || state.last.status === 'error')) set({ last: { ...state.last, status: 'sent', ...(reason ? { reasonSent: true } : {}) } })
  } catch (e) {
    Sentry.captureException(e)
    if (state.last?.id === id && (state.last.status === 'sent' || state.last.status === 'error')) set({ last: { ...state.last, status: 'error' } })
  }
}

/**
 * Undo the last Not for me (B-120: a page that moved under a finger made people hit it by mistake). The server puts the
 * swipe back as it was and forgets the taps (signal `undo_not_for_me`); only then does the card come back here, so the list
 * and the server never disagree. A failure keeps it hidden and offers a retry.
 */
export async function undoLast(): Promise<void> {
  const l = state.last
  if (!l || l.status === 'undoing' || l.status === 'back') return
  set({ last: { ...l, status: 'undoing' } })
  try {
    await pending.get(l.id)
    await signal({ kind: 'undo_not_for_me', item: l.id })
    const hidden = new Set(state.hidden)
    hidden.delete(l.id)
    writeJson(HIDDEN_KEY, [...hidden].slice(-MAX_KEPT))
    dropBrowseCache()
    set({ hidden, ...(state.last?.id === l.id ? { last: { ...state.last, status: 'back' as const } } : {}) })
  } catch (e) {
    Sentry.captureException(e)
    if (state.last?.id === l.id) set({ last: { ...state.last, status: 'undo_error' } })
  }
}

/** The optional reason after a Not for me: the same signal again with the reason (the server keeps the latest on the swipe). */
export function giveReason(reason: NfmReason) {
  const l = state.last
  if (!l || (l.status !== 'sent' && l.status !== 'error')) return
  set({ last: { ...l, reason } })
  void send(l.id, reason)
}
/** Retry the last Not for me (with its reason, if one was picked). */
export function retryLast() {
  const l = state.last
  if (l) void send(l.id, l.reason)
}
export function dismissLast() { set({ last: null }) }

/** A Browse answer without what they said is not for them (the list on screen and any cached answer). */
export function withoutHidden(res: BrowseRes | null, hidden: Set<string>): BrowseRes | null {
  if (!res || hidden.size === 0) return res
  const keep = (c: Card) => !hidden.has(c.item.id)
  const items = res.items.filter(keep)
  return { ...res, items, rows: res.rows.map((r) => ({ ...r, items: r.items.filter(keep) })), total: Math.max(0, res.total - (res.items.length - items.length)) }
}

// a change of person in this tab (sign-in, sign-out, delete, a new visitor): drop what the last person marked (lib/owner.ts)
onOwnerChange(() => { pending.clear(); set({ marks: {}, hidden: new Set(), last: null }) })
